/**
 * Top Bar Container Primitive
 *
 * Handles positioning and dimensions for top bars.
 * - Sticky positioning at top
 * - Fixed height (84px)
 * - Background and border styling
 * - Z-index layering
 *
 * Does NOT handle:
 * - Internal layout (use TopBarLayout for structure)
 * - Animations (not needed for persistent top bars)
 */

import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { LAYOUT_DIMENSIONS } from '../../constants/layout';

export interface TopBarContainerProps {
  /**
   * Content to display in the top bar
   */
  children: React.ReactNode;
}

/**
 * TopBarContainer - Positioning primitive for top bars
 *
 * Provides consistent positioning, dimensions, and styling for all top bars.
 * Use TopBarLayout inside this for internal structure.
 *
 * @example
 * ```tsx
 * <TopBarContainer>
 *   <TopBarLayout left={...} right={...} menuItems={...} />
 * </TopBarContainer>
 * ```
 */
export const TopBarContainer = React.forwardRef<HTMLDivElement, TopBarContainerProps>(
  ({ children }, ref) => {
    const theme = useTheme();

    return (
      <Box
        ref={ref}
        sx={{
          // No sticky here - parent container handles sticky behavior
          height: LAYOUT_DIMENSIONS.TOP_BAR_HEIGHT,
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.appBar - 1,
        }}
      >
        {children}
      </Box>
    );
  }
);

TopBarContainer.displayName = 'TopBarContainer';
